/**
 * Year-end profit planning for Cockpit.
 *
 * Progress billing means QB income is when we invoiced, not what we sold.
 * CRM holds bookings and the remaining billing schedule. QB holds real
 * billings and costs that already hit the P&L.
 */

import type { ChangeOrder, EnhancedDeal, SalesOrder } from '../types';
import type { CashflowMonthlyTotals } from '../types/finances';
import type { ForecastBucket } from './financesForecast';
import { toMonthKey } from './financesForecast';
import { getDealCloseInstant } from './leadAnalytics';
import type { ReadyToBillRow } from './readyToBill';

export interface QbYearToDate {
  income: number;
  cogs: number;
  expense: number;
  net: number;
}

export interface CrmSoldInYear {
  contractSigned: number;
  changeOrders: number;
  total: number;
}

function roundMoney(n: number): number {
  return Math.round(Number(n) || 0);
}

function toDate(value: unknown): Date | null {
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value;
  if (value && typeof value === 'object' && 'toDate' in value && typeof (value as { toDate: () => Date }).toDate === 'function') {
    const d = (value as { toDate: () => Date }).toDate();
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (typeof value === 'string' || typeof value === 'number') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
}

function monthKeyYear(key: string): number | null {
  const year = Number(String(key).slice(0, 4));
  return Number.isFinite(year) ? year : null;
}

function isOpenReceivable(row: Pick<ReadyToBillRow, 'qbPaid' | 'qbInvoiced' | 'tracked'>): boolean {
  if (row.qbPaid || row.qbInvoiced) return false;
  const status = row.tracked?.status;
  return !status || status === 'ready';
}

function baseSalesOrderForDeal(
  dealId: string,
  salesOrders: Array<Pick<SalesOrder, 'dealId' | 'grandTotal' | 'signedAt' | 'isChangeOrder' | 'changeOrderId'>>,
): Pick<SalesOrder, 'grandTotal'> | undefined {
  const bases = salesOrders
    .filter((so) => so.dealId === dealId && !so.isChangeOrder && !so.changeOrderId)
    .slice()
    .sort((a, b) => {
      const at = toDate(a.signedAt)?.getTime() ?? 0;
      const bt = toDate(b.signedAt)?.getTime() ?? 0;
      return bt - at;
    });
  return bases[0];
}

/** Real QB P&L dollars for a calendar year (progress billings and costs). */
export function qbYearToDate(
  year: number,
  months: string[] | undefined,
  totals: Record<string, CashflowMonthlyTotals> | undefined,
): QbYearToDate {
  const empty: QbYearToDate = { income: 0, cogs: 0, expense: 0, net: 0 };
  if (!months?.length || !totals) return empty;
  return months.reduce((acc, key) => {
    if (monthKeyYear(key) !== year) return acc;
    const row = totals[key];
    if (!row) return acc;
    acc.income += Number(row.income) || 0;
    acc.cogs += Number(row.cogs) || 0;
    acc.expense += Number(row.expense) || 0;
    acc.net += Number(row.netIncome) || 0;
    return acc;
  }, empty);
}

/** Work sold this year: signed base contracts + approved change orders. */
export function crmSoldInYear(
  year: number,
  deals: Array<Pick<EnhancedDeal, 'id' | 'outcome' | 'value' | 'closedAt' | 'updatedAt' | 'stageHistory'>>,
  salesOrders: Array<Pick<SalesOrder, 'dealId' | 'grandTotal' | 'signedAt' | 'isChangeOrder' | 'changeOrderId'>>,
  changeOrders: Array<Pick<ChangeOrder, 'status' | 'approvedAt' | 'deltaAmount'>>,
): CrmSoldInYear {
  let contractSigned = 0;
  for (const deal of deals) {
    if (deal.outcome !== 'won' || !deal.id) continue;
    const close = getDealCloseInstant(deal as EnhancedDeal);
    if (close == null) continue;
    if (new Date(close).getFullYear() !== year) continue;
    const so = baseSalesOrderForDeal(deal.id, salesOrders);
    contractSigned += so ? Number(so.grandTotal) || 0 : Number(deal.value) || 0;
  }

  let coTotal = 0;
  for (const co of changeOrders) {
    if (co.status !== 'approved') continue;
    const approved = toDate(co.approvedAt);
    if (!approved || approved.getFullYear() !== year) continue;
    coTotal += Number(co.deltaAmount) || 0;
  }

  return {
    contractSigned: roundMoney(contractSigned),
    changeOrders: roundMoney(coTotal),
    total: roundMoney(contractSigned + coTotal),
  };
}

/** CRM receivables still due in the rest of the year and not yet invoiced in QB. */
export function crmStillToBillInYear(
  year: number,
  moneyInRows: Array<Pick<ReadyToBillRow, 'amount' | 'dueDate' | 'qbPaid' | 'qbInvoiced' | 'tracked'>>,
  now: Date = new Date(),
): number {
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = new Date(year, 11, 31, 23, 59, 59, 999);
  if (year < now.getFullYear()) return 0;
  return roundMoney(moneyInRows.reduce((sum, row) => {
    if (!isOpenReceivable(row) || !row.dueDate) return sum;
    if (row.dueDate < start || row.dueDate > end) return sum;
    if (row.dueDate.getFullYear() !== year) return sum;
    return sum + (Number(row.amount) || 0);
  }, 0));
}

/** Open CRM AR not yet invoiced — backlog, not a year-end run-rate. */
export function crmUnbilledBacklog(
  moneyInRows: Array<Pick<ReadyToBillRow, 'amount' | 'qbPaid' | 'qbInvoiced' | 'tracked'>>,
): number {
  return roundMoney(moneyInRows.reduce((sum, row) => (
    isOpenReceivable(row) ? sum + (Number(row.amount) || 0) : sum
  ), 0));
}

/** Forecast overhead + payroll still in this calendar year (includes the current month). */
export function crmRemainingBurn(
  year: number,
  buckets: Array<Pick<ForecastBucket, 'month' | 'total'>>,
  now: Date = new Date(),
): number {
  const startKey = toMonthKey(new Date(now.getFullYear(), now.getMonth(), 1));
  return roundMoney(buckets.reduce((sum, b) => {
    if (b.month.getFullYear() !== year) return sum;
    if (toMonthKey(b.month) < startKey) return sum;
    return sum + (Number(b.total) || 0);
  }, 0));
}

export interface RemainingJobOutflows {
  ap: number;
  job: number;
  capex: number;
}

export type ProfitOutflowMonth = {
  month: Date;
  apOut: number;
  jobOut: number;
  items?: Array<{ kind: string; plannedKind?: string; amount: number }>;
};

function monthCapexOut(month: ProfitOutflowMonth): number {
  return (month.items ?? []).reduce((sum, item) => {
    if (item.kind !== 'planned' || item.plannedKind !== 'other') return sum;
    return sum + (Number(item.amount) || 0);
  }, 0);
}

/**
 * Open AP, leftover job costs, and approved CapEx still in this calendar year.
 * Same month buckets as the cash forecast (overdue AP lands in the current month).
 * Bonus and tax stay out — those are uses of profit, not cost to earn it.
 */
export function remainingJobOutflowsInYear(
  year: number,
  months: ProfitOutflowMonth[],
  now: Date = new Date(),
): RemainingJobOutflows {
  const startKey = toMonthKey(new Date(now.getFullYear(), now.getMonth(), 1));
  const acc = months.reduce<RemainingJobOutflows>((sum, m) => {
    if (m.month.getFullYear() !== year) return sum;
    if (toMonthKey(m.month) < startKey) return sum;
    return {
      ap: sum.ap + (Number(m.apOut) || 0),
      job: sum.job + (Number(m.jobOut) || 0),
      capex: sum.capex + monthCapexOut(m),
    };
  }, { ap: 0, job: 0, capex: 0 });
  return { ap: roundMoney(acc.ap), job: roundMoney(acc.job), capex: roundMoney(acc.capex) };
}

/** Taxable close: books so far + remaining AR − burn − AP − job costs − approved CapEx. */
export function impliedTaxableClose(args: {
  qbYtdNet: number;
  stillToBill: number;
  remainingBurn: number;
  remainingAp?: number;
  remainingJobCosts?: number;
  remainingCapex?: number;
}): number {
  return roundMoney(
    (Number(args.qbYtdNet) || 0)
    + (Number(args.stillToBill) || 0)
    - (Number(args.remainingBurn) || 0)
    - (Number(args.remainingAp) || 0)
    - (Number(args.remainingJobCosts) || 0)
    - (Number(args.remainingCapex) || 0),
  );
}
